// Phone-number utilities for phone-OTP sign-in.
//
// UX contract:
//   * The customer picks a country from a small dropdown; the country
//     defaults to United States / Canada (+1).
//   * The customer types their local mobile number in whatever format
//     they normally do (spaces, hyphens, parentheses, dots, or none).
//   * We strip common formatting characters and normalize the final
//     value to strict E.164 (leading '+' and country-code digits) so
//     Supabase Auth / Twilio Verify receives a valid international
//     number.
//   * If the customer types their own '+' + country code, we accept it
//     verbatim (their explicit country selection wins over the
//     dropdown when the raw text already starts with '+').

const E164_RE = /^\+[1-9]\d{7,14}$/;

// Small curated list.  Order matters — the first entry is the default.
// Keep this short and predictable; more countries can be added later.
export const COUNTRY_CODES = [
  { code: '+1',  label: 'United States / Canada', example: '346 848 1414' },
  { code: '+52', label: 'Mexico',                 example: '55 1234 5678' },
  { code: '+44', label: 'United Kingdom',         example: '7400 123456' },
  { code: '+33', label: 'France',                 example: '6 12 34 56 78' },
  { code: '+49', label: 'Germany',                example: '30 12345678' },
  { code: '+34', label: 'Spain',                  example: '612 34 56 78' },
  { code: '+61', label: 'Australia',              example: '412 345 678' },
  { code: '+91', label: 'India',                  example: '98765 43210' },
  { code: '+81', label: 'Japan',                  example: '90 1234 5678' },
  { code: '+55', label: 'Brazil',                 example: '11 91234 5678' },
  { code: '+27', label: 'South Africa',           example: '71 234 5678' },
];

export const DEFAULT_COUNTRY_CODE = '+1';

// Strip everything except digits from a raw string.
function digitsOnly(s) {
  return (s || '').replace(/[^\d]/g, '');
}

// True if the raw input contains any obviously invalid character
// (letters, symbols other than the accepted formatting characters or a
// single leading '+').  Formatting characters allowed: spaces, dashes,
// parentheses, dots.
function hasInvalidChars(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  // Allow: optional single leading '+', digits, spaces, dashes,
  // parens, dots.
  return !/^\+?[\d\s().\-]*$/.test(s);
}

// Normalize a raw customer-entered phone number using a selected
// country dial code to a strict E.164 string.  Returns:
//   { ok: true,  value: '+13468481414', error: null }
// or
//   { ok: false, value: null,           error: 'Enter a valid mobile number.' }
//
// Rules:
//   * If raw starts with '+', we take the customer's number as-is
//     (their explicit intent) and validate against E.164.
//   * Otherwise we prepend the selected country dial code.
//   * For country '+1', we also accept a leading '1' the customer
//     typed themselves (e.g. 1-346-848-1414).
//   * For countries other than '+1', a leading '0' is a national
//     trunk prefix and is stripped before prepending the country code.
export function normalizeToE164(raw, countryCode = DEFAULT_COUNTRY_CODE) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) {
    return { ok: false, value: null, error: 'Enter a valid mobile number.' };
  }
  if (hasInvalidChars(trimmed)) {
    return { ok: false, value: null, error: 'Enter a valid mobile number.' };
  }

  let e164;

  if (trimmed.startsWith('+')) {
    // Customer typed their own country prefix.
    e164 = '+' + digitsOnly(trimmed);
  } else {
    const cc = (countryCode || DEFAULT_COUNTRY_CODE).replace(/[^\d+]/g, '');
    const ccDigits = cc.replace(/^\+/, '');
    let local = digitsOnly(trimmed);
    if (!ccDigits || !local) {
      return { ok: false, value: null, error: 'Enter a valid mobile number.' };
    }
    if (cc === '+1') {
      // Accept 10-digit or 11-digit starting with 1.
      if (local.length === 11 && local.startsWith('1')) local = local.slice(1);
    } else {
      // Strip a single leading trunk zero for other countries.
      if (local.startsWith('0')) local = local.replace(/^0+/, '');
    }
    e164 = '+' + ccDigits + local;
  }

  if (!E164_RE.test(e164)) {
    return { ok: false, value: null, error: 'Enter a valid mobile number.' };
  }
  return { ok: true, value: e164, error: null };
}

// Legacy strict validator kept for internal callers/tests that want
// to validate an already-E.164 string on its own.
export function validateE164(raw) {
  const s = String(raw || '').trim();
  if (!s) return { ok: false, value: '', error: 'Enter a valid mobile number.' };
  const compact = '+' + digitsOnly(s);
  if (!E164_RE.test(compact) || !s.startsWith('+')) {
    return { ok: false, value: s, error: 'Enter a valid mobile number.' };
  }
  return { ok: true, value: compact, error: null };
}

// Retained for backwards compatibility with any existing importer.
export function normalizePhoneInput(raw) {
  const s = String(raw || '').trim();
  const hasPlus = s.startsWith('+');
  const digits = digitsOnly(s);
  return hasPlus ? '+' + digits : digits;
}
